import axios from 'axios'
import { useAuth } from '@/composables/useAuth'
import { useEprintFunnel } from '@/composables/format-edit/useEprintFunnel'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type {
  EprintActions,
  EprintSession
} from '@/composables/format-edit/useEprintSession'
import { createEprintFilename } from '@/utils/format-edit/eprintFilename'
import type { AxiosLikeError } from '@/utils/format-edit/eprintPdfMessages'

export function useEprintPaypay(
  session: EprintSession,
  actions: EprintActions
) {
  const { user } = useAuth()
  const { deviceLib, varuna, site, formLib, message } = useHostServices()
  const funnel = useEprintFunnel()

  function filename() {
    return createEprintFilename({
      formatId: session.formatid,
      ext: 'pdf',
      dateFormat: formLib.dateFormat
    })
  }

  function appendPaypayFields(formData: FormData) {
    formData.append('uuid', user.value?.uid || '')
    formData.append('payment_cost', String(session.eprintPrice))
    formData.append('print_type', session.printType)
    formData.append('format_id', session.formatid)
    formData.append('filename', filename())
    formData.append('app_html', session.previewHtml)
    formData.append('device_flg', deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('app_flag', site.appFlag)
    formData.append('font_name', session.selFontStyle4DB)
    formData.append('version', '2')
  }

  function savePaypayEprintTempData(
    merchantPaymentId: string,
    url: string,
    expiryDate?: number
  ) {
    const formData = new FormData()
    appendPaypayFields(formData)
    formData.append('merchant_payment_id', merchantPaymentId)

    actions
      .savePaymentLog('paypay', session.eprintPrice, 'pending')
      .then(() =>
        axios.post(varuna.endPoints.savePaypayEprintTempdata, formData, {
          timeout: 60000
        })
      )
      .then((response) => {
        if (response.data.status === 'success') {
          message({ message: 'PayPayページに遷移します', type: 'success' })
          localStorage.setItem('paypay_eprint_merchant_id', merchantPaymentId)
          localStorage.setItem(
            'paypay_eprint_data',
            JSON.stringify({
              printType: session.printType,
              formatId: session.formatid,
              fileName: formData.get('filename'),
              paymentCost: session.eprintPrice,
              previewHtml: session.previewHtml,
              fontName: session.selFontStyle4DB,
              merchantPaymentId,
              expiryAt: expiryDate
                ? Number(expiryDate) * 1000
                : Date.now() + session.paypayCodeExpiryFallbackMs
            })
          )
          session.paypayEprintUrl = url
          session.merchantPaymentId = merchantPaymentId
          session.paypayNoticeVisible = true
        } else {
          message({
            message:
              response.data.message ||
              '印刷データの準備に失敗しました。お手数ですが、もう一度お試しください。',
            type: 'error'
          })
          actions.updatePaymentLogStatus('failed')
        }
      })
      .catch((error) => {
        message({
          message:
            '印刷データの準備に失敗しました。お手数ですが、もう一度お試しください。',
          type: 'error'
        })
        console.error(error)
        actions.updatePaymentLogStatus('failed')
      })
  }

  function handleGetPaypayCodeEprint() {
    const formData = new FormData()
    appendPaypayFields(formData)
    formData.append('redirect_url', window.location.href)

    axios
      .post(varuna.endPoints.getPaypaycodeEprint, formData, { timeout: 60000 })
      .then((response) => {
        if (
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.url
        ) {
          funnel.send('paypay_code_received', {
            payment_method: 'paypay',
            merchant_payment_id: response.data.merchant_payment_id,
            price: session.eprintPrice,
            print_type: session.printType,
            format_id: session.formatid,
            funnel_session_id: session.funnelSessionId
          })
          savePaypayEprintTempData(
            response.data.merchant_payment_id,
            response.data.data.url,
            response.data.data.expiryDate
          )
        } else {
          funnel.send('paypay_code_error', {
            payment_method: 'paypay',
            error_message: 'PayPay決済コードの取得に失敗しました',
            price: session.eprintPrice,
            print_type: session.printType,
            format_id: session.formatid,
            funnel_session_id: session.funnelSessionId
          })
          message({
            message: 'PayPay決済コードの取得に失敗しました',
            type: 'error'
          })
        }
      })
      .catch((raw) => {
        const error = raw as AxiosLikeError
        let errorMessage = 'PayPay決済コードの取得に失敗しました'
        let errorCode = 'unknown'
        if (error.response) {
          errorCode = 'server_' + error.response.status
          if (error.response.status >= 500) {
            errorMessage =
              'サーバーエラーが発生しました。しばらく待ってから再度お試しください。'
          } else if (error.response.status === 404) {
            errorMessage = 'PayPay決済サービスが利用できません。'
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message
          }
        } else if (
          error.code === 'ECONNABORTED' ||
          (error.message || '').includes('timeout')
        ) {
          errorCode = 'timeout'
          errorMessage =
            'PayPay決済サーバーとの通信がタイムアウトしました。しばらく待ってから再度お試しください。'
        } else if (
          error.code === 'NETWORK_ERROR' ||
          (error.message || '').includes('Network Error')
        ) {
          errorCode = 'network_error'
          errorMessage =
            'ネットワークエラーが発生しました。インターネット接続を確認してください。'
        }
        funnel.send('paypay_code_error', {
          payment_method: 'paypay',
          error_code: errorCode,
          error_message: errorMessage,
          price: session.eprintPrice,
          print_type: session.printType,
          format_id: session.formatid,
          funnel_session_id: session.funnelSessionId
        })
        message({
          message: errorMessage,
          type: 'error',
          duration: 0,
          showClose: true
        })
      })
  }

  function openPayPayEprintSite() {
    actions.completePaypayEprint(session.merchantPaymentId)
    window.location.href = session.paypayEprintUrl
  }

  return {
    savePaypayEprintTempData,
    handleGetPaypayCodeEprint,
    openPayPayEprintSite
  }
}
